import { type Camera, OrthographicCamera, Vector3 } from 'three';

import { LOD_DISTANCE_THRESHOLDS_SQ } from 'shared/constants';

const ORTHO_PLANAR_WEIGHT_SQ = 1.0;
const ORTHO_DEPTH_WEIGHT_SQ = 0.01;
const ORTHO_FRUSTUM_WEIGHT_SQ = 0.36;
const LOD_HYSTERESIS_RATIO = 0.12;
const tmpDiff = new Vector3();
const tmpForward = new Vector3();

export function getLodLevelByDistanceSq(distanceSq: number): number {
    if (distanceSq < LOD_DISTANCE_THRESHOLDS_SQ[0]) {
        return 0;
    }
    if (distanceSq < LOD_DISTANCE_THRESHOLDS_SQ[1]) {
        return 1;
    }
    return 2;
}

export function getLodLevelByDistanceSqWithHysteresis(
    distanceSq: number,
    currentLevel: number | null | undefined
): number {
    if (currentLevel === null || currentLevel === undefined || Number.isNaN(currentLevel)) {
        return getLodLevelByDistanceSq(distanceSq);
    }

    const [threshold0, threshold1] = LOD_DISTANCE_THRESHOLDS_SQ;
    const exitLod1ToLod0 = threshold0 * (1 - LOD_HYSTERESIS_RATIO);
    const enterLod1FromLod0 = threshold0 * (1 + LOD_HYSTERESIS_RATIO);
    const exitLod2ToLod1 = threshold1 * (1 - LOD_HYSTERESIS_RATIO);
    const enterLod2FromLod1 = threshold1 * (1 + LOD_HYSTERESIS_RATIO);

    if (currentLevel <= 0) {
        return distanceSq >= enterLod1FromLod0 ? 1 : 0;
    }

    if (currentLevel === 1) {
        if (distanceSq < exitLod1ToLod0) {
            return 0;
        }
        if (distanceSq >= enterLod2FromLod1) {
            return 2;
        }
        return 1;
    }

    return distanceSq < exitLod2ToLod1 ? 1 : 2;
}

/**
 * Returns an effective squared distance for LOD selection.
 * For perspective cameras: Euclidean world-space distance squared.
 * For orthographic cameras: combines per-instance planar/depth offsets and
 * frustum scale using squared terms to avoid expensive per-instance transforms.
 */
export function getEffectiveDistanceSq(camera: Camera, cameraPosition: Vector3, point: Vector3): number {
    if (camera instanceof OrthographicCamera) {
        const zoom = Math.max(camera.zoom, 0.0001);
        const halfViewWidth = Math.abs((camera.right - camera.left) * 0.5) / zoom;
        const halfViewHeight = Math.abs((camera.top - camera.bottom) * 0.5) / zoom;
        const frustumScaleSq = Math.max(halfViewWidth * halfViewWidth, halfViewHeight * halfViewHeight);

        const diff = tmpDiff.copy(point).sub(cameraPosition);
        const diffLengthSq = diff.lengthSq();
        const forward = camera.getWorldDirection(tmpForward);
        const depth = diff.dot(forward);
        const depthSq = depth * depth;
        const planarSq = Math.max(0, diffLengthSq - depthSq);

        return (
            planarSq * ORTHO_PLANAR_WEIGHT_SQ +
            depthSq * ORTHO_DEPTH_WEIGHT_SQ +
            frustumScaleSq * ORTHO_FRUSTUM_WEIGHT_SQ
        );
    }

    return cameraPosition.distanceToSquared(point);
}

export function getInstanceLodLevel(camera: Camera | null | undefined, point: Vector3, isNavigator = false): number {
    if (!camera) {
        return isNavigator ? 1 : 0;
    }

    const lodLevel = getLodLevelByDistanceSq(getEffectiveDistanceSq(camera, camera.position, point));
    return isNavigator ? Math.max(1, lodLevel) : lodLevel;
}
